import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import {
  adminGrantUserPermissions,
  adminRevokeUserPermissions,
  grantUserPermission,
  revokeUserPermission,
} from "../api/user.api";
import {
  CreatePermissionsBody,
  RevokePermissionsBody,
} from "../api/models/permission.models";
import { UIState } from "../types/ui-types";
import {
  adminFetchUserPermissions,
  fetchUserPermissions,
} from "../api/userRights.api";
import { useState } from "react";
import { UserRight } from "../api/models/userRights.models";

const useAllOrganizationPermissions = ({
  userId,
  companyId,
  organizationIds = [],
  isAdmin = false,
}: {
  userId: number;
  companyId?: number;
  organizationIds?: number[];
  isAdmin?: boolean;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const [permissionsData, setPermissionsData] = useState<
    (UserRight & { companyId?: number; organizationId?: number })[]
  >([]);
  const [permissionsIsLoading, setPermissionsIsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState<Error | null>(null);

  const fetchAllPermissions = async () => {
    return isAdmin
      ? adminFetchAllPermissionsMutation.mutateAsync()
      : fetchAllPermissionsMutation.mutateAsync();
  };

  // Mutation to fetch all permissions as admin
  const adminFetchAllPermissionsMutation = useMutation({
    mutationFn: async () => {
      setPermissionsIsLoading(true);
      setPermissionsError(null);

      return adminFetchUserPermissions(bearerToken as string, userId)
        .then((data) => {
          setPermissionsData(data);
          return data;
        })
        .catch((error) => {
          setPermissionsError(error);
          throw error;
        })
        .finally(() => {
          setPermissionsIsLoading(false);
        });
    },
  });

  // Mutation to fetch all permissions
  const fetchAllPermissionsMutation = useMutation({
    mutationFn: async () => {
      setPermissionsIsLoading(true);
      setPermissionsError(null);

      const promises: Promise<{
        data: UserRight[];
        companyId?: number;
        organizationId?: number;
      }>[] = [];

      // Add company-level permissions fetch
      if (companyId) {
        promises.push(
          fetchUserPermissions(
            bearerToken as string,
            userId,
            companyId as number,
            0
          ).then((data) => ({ data, companyId, organizationId: undefined }))
        );
      }

      // Add organization-specific permissions fetches
      organizationIds.forEach((organizationId) => {
        promises.push(
          fetchUserPermissions(
            bearerToken as string,
            userId,
            companyId as number,
            organizationId
          ).then((data) => ({ data, companyId, organizationId }))
        );
      });

      return Promise.all(promises)
        .then((results) => {
          const allPermissions = results.flatMap((result) =>
            result.data.map((permission) => ({
              ...permission,
              companyId: result.companyId,
              organizationId: result.organizationId,
            }))
          );
          setPermissionsData(allPermissions);
          return allPermissions;
        })
        .catch((error) => {
          setPermissionsError(error);
          throw error;
        })
        .finally(() => {
          setPermissionsIsLoading(false);
        });
    },
  });

  const grantPermissionsMutation = useMutation({
    mutationFn: (payload: CreatePermissionsBody) =>
      companyId
        ? grantUserPermission(bearerToken as string, payload, companyId)
        : adminGrantUserPermissions(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to grant permissions:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-permissions-grant-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-permissions-granted"),
        life: 3000,
      });
      // Refetch all permissions
      fetchAllPermissionsMutation.mutate();
    },
  });

  const grantPermissions = async (payload: CreatePermissionsBody) => {
    const result = await grantPermissionsMutation.mutateAsync(payload);
    return result;
  };

  const revokePermissionsMutation = useMutation({
    mutationFn: (payload: RevokePermissionsBody) =>
      companyId
        ? revokeUserPermission(bearerToken as string, payload, companyId)
        : adminRevokeUserPermissions(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to revoke permissions:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-permissions-revoke-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      // Refetch all permissions
      fetchAllPermissionsMutation.mutate();
    },
  });

  const revokePermissions = async (payload: RevokePermissionsBody) => {
    const result = await revokePermissionsMutation.mutateAsync(payload);
    return result;
  };

  return {
    fetchAllPermissions,
    grantPermissions,
    isGrantingPermissions: grantPermissionsMutation.isPending,
    error: grantPermissionsMutation.error,
    permissionsData,
    permissionsError,
    permissionsIsLoading,
    revokePermissions,
    isRevokingPermissions: revokePermissionsMutation.isPending,
  };
};

export default useAllOrganizationPermissions;
